// Hjælpemodul der parser en pastet tabel (Excel-style) og matcher
// personer + fag mod skolens brugere og standardfagene.
// Output er et array af 'rows' som UI-en kan iterere over.

const SUBJECT_LEVEL_LINJEFAG = 'L'
const SUBJECT_LEVEL_TILSVARENDE = 'K'
const MAX_COMPETENCIES = 6

// Aliasser for fagnavne der ofte skrives anderledes end den officielle STIL-titel.
// Værdien er det fulde 'name' fra standardSubjects-listen.
const SUBJECT_ALIASES = {
  'religion': 'Kristendomskundskab/religion',
  'kristendom': 'Kristendomskundskab',
  'kristendomskundskab religion': 'Kristendomskundskab/religion',
  'n/t': 'Natur/teknik',
  'nt': 'Natur/teknik',
  'natur og teknik': 'Natur/teknik',
  'natur og teknologi': 'Natur/teknik',
  'natur teknologi': 'Natur/teknik',
  'natur/teknologi': 'Natur/teknik',
  'natur teknik': 'Natur/teknik',
  'fysik': 'Fysik/kemi',
  'kemi': 'Fysik/kemi',
  'fysik kemi': 'Fysik/kemi',
  'haandarbejde': 'Håndarbejde',
  'haandvaerk og design': 'Håndværk og design',
  'sloejd': 'Sløjd',
  'idraet': 'Idræt',
  'boernehaveklasse': 'Børnehaveklasse',
  'bhk': 'Børnehaveklasse',
  'tysk valgfag': 'Tysk (valgfag)',
  'tysk tilbudsfag': 'Tysk (tilbudsfag)',
  'fransk valgfag': 'Fransk (valgfag)',
  'fransk tilbudsfag': 'Fransk (tilbudsfag)'
}

function normalize (str) {
  if (str === null || str === undefined) {
    return ''
  }
  return String(str)
    .replace(/\u00A0/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

// Strip parentes-suffix for at gøre 'Tysk (tilbudsfag)' og 'Tysk' til samme kerne.
// Beholder dog parentes-segmenter der ligner et niveau-suffix '(L)' / '(K)'.
function stripQualifier (normalized) {
  return normalized.replace(/\s*\([^)]*\)\s*$/g, '').trim()
}

function detectLevelSuffix (raw) {
  const m = String(raw).match(/\((L|K|l|k)\)\s*$/)
  if (m) {
    return {
      level: m[1].toUpperCase(),
      stripped: raw.replace(/\s*\((L|K|l|k)\)\s*$/, '').trim()
    }
  }
  return null
}

function splitRowsAndCells (text) {
  if (!text) {
    return []
  }
  const cleaned = String(text).replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n')
  const lines = cleaned.split('\n')
  const rows = []
  lines.forEach(line => {
    if (line.trim().length < 1) {
      return
    }
    const cells = line.split('\t').map(cell => cell.trim())
    rows.push(cells)
  })
  return rows
}

function isHeaderRow (cells) {
  if (cells.length < 1) {
    return false
  }
  const first = normalize(cells[0])
  return first === 'navn' || first === 'name' || first === 'lærer' || first === 'laerer'
}

function splitSubjectList (cell) {
  if (!cell || cell.trim().length < 1) {
    return []
  }
  return cell.split(/[,;]/).map(s => s.trim()).filter(s => s.length > 0)
}

function buildSubjectIndex (standardSubjects) {
  const byName = {}
  const byStripped = {}
  standardSubjects.forEach(subject => {
    const normName = normalize(subject.name)
    byName[normName] = subject
    const stripped = stripQualifier(normName)
    if (!byStripped[stripped]) {
      byStripped[stripped] = []
    }
    byStripped[stripped].push(subject)
  })
  return { byName: byName, byStripped: byStripped }
}

function buildUserIndex (users) {
  const byName = {}
  users.forEach(user => {
    const normName = normalize(user.name)
    if (!byName[normName]) {
      byName[normName] = []
    }
    byName[normName].push(user)
  })
  return byName
}

function matchUser (rawName, userIndex) {
  const norm = normalize(rawName)
  if (norm.length < 1) {
    return { status: 'user_not_found', match: null, candidates: [] }
  }
  const exact = userIndex[norm]
  if (exact && exact.length === 1) {
    return { status: 'ok', match: exact[0], candidates: exact }
  }
  if (exact && exact.length > 1) {
    return { status: 'user_multiple', match: null, candidates: exact }
  }
  // Forsøg substring-match (fx pasten har mellemnavn med, eller mangler det)
  const candidates = []
  Object.keys(userIndex).forEach(key => {
    if (key.indexOf(norm) > -1 || norm.indexOf(key) > -1) {
      userIndex[key].forEach(u => candidates.push(u))
    }
  })
  if (candidates.length === 1) {
    return { status: 'ok', match: candidates[0], candidates: candidates }
  }
  if (candidates.length > 1) {
    return { status: 'user_multiple', match: null, candidates: candidates }
  }
  return { status: 'user_not_found', match: null, candidates: [] }
}

function matchSubject (rawSubject, defaultLevel, subjectIndex) {
  const levelSuffix = detectLevelSuffix(rawSubject)
  let queryRaw = rawSubject
  let level = defaultLevel
  if (levelSuffix) {
    queryRaw = levelSuffix.stripped
    level = levelSuffix.level
  }
  const norm = normalize(queryRaw)
  const base = {
    raw: rawSubject,
    level: level,
    status: 'subject_not_found',
    match: null,
    candidates: []
  }
  if (norm.length < 1) {
    return base
  }
  // Direkte navn-match
  if (subjectIndex.byName[norm]) {
    return Object.assign(base, { status: 'ok', match: subjectIndex.byName[norm], candidates: [subjectIndex.byName[norm]] })
  }
  // Alias-match
  const aliasName = SUBJECT_ALIASES[norm]
  if (aliasName) {
    const aliased = subjectIndex.byName[normalize(aliasName)]
    if (aliased) {
      return Object.assign(base, { status: 'ok', match: aliased, candidates: [aliased] })
    }
  }
  // Match efter fjernelse af parentes-kvalifikator på begge sider
  const strippedNorm = stripQualifier(norm)
  if (subjectIndex.byStripped[strippedNorm]) {
    const list = subjectIndex.byStripped[strippedNorm]
    if (list.length === 1) {
      return Object.assign(base, { status: 'ok', match: list[0], candidates: list })
    }
    return Object.assign(base, { status: 'subject_multiple', match: null, candidates: list })
  }
  return base
}

function parseRow (cells, defaults, userIndex, subjectIndex) {
  const rawName = cells[0] || ''
  const rawLinjefag = cells[1] || ''
  const rawTilsvarende = cells[2] || ''

  const userResult = matchUser(rawName, userIndex)

  const linjefagItems = splitSubjectList(rawLinjefag).map(raw => {
    return matchSubject(raw, SUBJECT_LEVEL_LINJEFAG, subjectIndex)
  })
  const tilsvarendeItems = splitSubjectList(rawTilsvarende).map(raw => {
    return matchSubject(raw, SUBJECT_LEVEL_TILSVARENDE, subjectIndex)
  })
  const subjects = linjefagItems.concat(tilsvarendeItems)

  const overLimit = subjects.length > MAX_COMPETENCIES
  const hasSubjectIssue = subjects.some(s => s.status !== 'ok')
  const isAmbiguous = userResult.status !== 'ok' || hasSubjectIssue || overLimit

  return {
    rawName: rawName,
    rawLinjefag: rawLinjefag,
    rawTilsvarende: rawTilsvarende,
    user: userResult,
    subjects: subjects,
    overLimit: overLimit,
    isAmbiguous: isAmbiguous,
    status: 'pending',
    error: null
  }
}

export function parsePastedTable (text, users, standardSubjects) {
  const rawRows = splitRowsAndCells(text)
  if (rawRows.length < 1) {
    return []
  }
  const startIdx = isHeaderRow(rawRows[0]) ? 1 : 0
  const userIndex = buildUserIndex(users || [])
  const subjectIndex = buildSubjectIndex(standardSubjects || [])
  const dataRows = rawRows.slice(startIdx)
  const rows = []
  dataRows.forEach(cells => {
    if (cells.length < 1 || cells.every(c => c.length < 1)) {
      return
    }
    rows.push(parseRow(cells, null, userIndex, subjectIndex))
  })
  return rows
}

// Hjælpere eksponeret til UI'en så den kan bygge fag-til-server-payload
// efter at brugeren evt. har afklaret tvivl.
export function buildCompetencyPayload (row) {
  const out = []
  row.subjects.forEach(subject => {
    if (subject.status !== 'ok' || !subject.match) {
      return
    }
    if (subject.skipped) {
      return
    }
    out.push({
      subject_code: subject.match.subject_code,
      level: subject.level
    })
  })
  return out
}

// Parser en simpel komma/newline/tab-separeret fag-paste mod en items-liste,
// hvor items er den allerede-niveauopdelte liste fra wrapper-komponenten,
// dvs. items har form: { name: 'Dansk (L)', subject_code, level: 'L'|'K', id }.
//
// Returnerer:
//   matched: items der blev matchet (med rawText og tilsvarende item).
//   notFound: raw tekstdele der ikke kunne matches.
//   ambiguous: raw tekstdele med flere mulige matches.
export function parseSubjectListPaste (text, defaultLevel, items) {
  const parts = splitSubjectList(String(text || '').replace(/\r\n?/g, '\n').replace(/[\n\t]/g, ','))
  const matched = []
  const notFound = []
  const ambiguous = []

  // Forbered indekser over leveled items.
  // Vi splitter hver items.name i basisnavn (uden trailing '(L)'/'(K)') + niveau (item.level).
  const indexed = items.map(item => {
    const baseRaw = String(item.name).replace(/\s*\((L|K)\)\s*$/i, '').trim()
    return {
      item: item,
      baseNormalized: normalize(baseRaw),
      strippedNormalized: stripQualifier(normalize(baseRaw))
    }
  })

  parts.forEach(rawPart => {
    const trimmed = rawPart.trim()
    if (trimmed.length < 1) {
      return
    }
    const levelSuffix = detectLevelSuffix(trimmed)
    let queryRaw = trimmed
    let level = defaultLevel
    if (levelSuffix) {
      queryRaw = levelSuffix.stripped
      level = levelSuffix.level
    }
    const norm = normalize(queryRaw)
    const aliasName = SUBJECT_ALIASES[norm]
    const targets = []
    if (aliasName) {
      targets.push(normalize(aliasName))
    }
    targets.push(norm)

    let candidates = []
    // 1) Direkte basenavn-match på items med korrekt niveau.
    targets.some(target => {
      candidates = indexed.filter(entry => entry.item.level === level && entry.baseNormalized === target)
      return candidates.length > 0
    })
    // 2) Strippet match (fjern parentes-kvalifikator) hvis intet direkte hit.
    if (candidates.length < 1) {
      const stripped = stripQualifier(norm)
      candidates = indexed.filter(entry => entry.item.level === level && entry.strippedNormalized === stripped)
    }

    if (candidates.length === 1) {
      matched.push({ raw: trimmed, item: candidates[0].item })
    } else if (candidates.length > 1) {
      ambiguous.push({ raw: trimmed, candidates: candidates.map(c => c.item) })
    } else {
      notFound.push({ raw: trimmed })
    }
  })

  return { matched: matched, notFound: notFound, ambiguous: ambiguous }
}

export const __testing = {
  normalize,
  stripQualifier,
  detectLevelSuffix,
  splitRowsAndCells,
  isHeaderRow,
  splitSubjectList,
  matchUser,
  matchSubject
}

export const constants = {
  SUBJECT_LEVEL_LINJEFAG: SUBJECT_LEVEL_LINJEFAG,
  SUBJECT_LEVEL_TILSVARENDE: SUBJECT_LEVEL_TILSVARENDE,
  MAX_COMPETENCIES: MAX_COMPETENCIES
}



// WEBPACK FOOTER //
// ./src/components/Reporting/KompetenceIndberetning/pasteTableMatcher.js